'use client';

import { c as _c } from "react/compiler-runtime";
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import GridView from '@/components/BlogPage/PostsList/GridView';
import { usePostsAnimation } from '@/components/BlogPage/PostsList/hooks/usePostsAnimation';
import { useViewMode } from '@/components/BlogPage/PostsList/hooks/useViewMode';
import ListViewModeToggle from '@/components/BlogPage/PostsList/ListViewModeToggle';
import TableView from '@/components/BlogPage/PostsList/TableView';
import TagsFilter from '@/components/BlogPage/PostsList/TagsFilter';
import { BlogPostDto, BlogTagDto } from '@/components/BlogPage/types';
import AnimatedButton from '@/components/shared/AnimatedButton';
import FilterEmptyResult from '@/components/shared/FilterEmptyResult';
import cn from '@/helpers/cn';
import useDevice from '@/hooks/useDevice';
import css from './style.module.scss';
interface Props {
  posts: BlogPostDto[];
  tags: BlogTagDto[];
  initialVisiblePostsCount?: number;
}
const getFilteredPosts = (posts: BlogPostDto[], selectedTags: string[]) => !selectedTags.length ? posts : posts.filter(post => post.tags.some(tag => selectedTags.includes(tag.name)));
const PostsList: FC<Props> = t0 => {
  const $ = _c(41);
  const {
    posts,
    tags,
    initialVisiblePostsCount
  } = t0;
  const device = useDevice();
  const isMobile = device === "mobile";
  const containerRef = useRef(null);
  const {
    viewMode,
    handleViewModeChange
  } = useViewMode(containerRef);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selectedTags, setSelectedTags] = useState(t1);
  const [visibleCount, setVisibleCount] = useState(initialVisiblePostsCount ?? Infinity);
  let t2;
  if ($[1] !== posts || $[2] !== selectedTags) {
    t2 = getFilteredPosts(posts, selectedTags);
    $[1] = posts;
    $[2] = selectedTags;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const filteredPosts = t2;
  let t3;
  if ($[4] !== filteredPosts || $[5] !== visibleCount) {
    t3 = filteredPosts.slice(0, visibleCount);
    $[4] = filteredPosts;
    $[5] = visibleCount;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const visiblePosts = t3;
  const hasMore = visibleCount < filteredPosts.length;
  let t4;
  if ($[7] !== viewMode || $[8] !== visiblePosts) {
    t4 = {
      containerRef,
      displayedPosts: visiblePosts,
      viewMode
    };
    $[7] = viewMode;
    $[8] = visiblePosts;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    prepareTagTransition
  } = usePostsAnimation(t4);
  let t5;
  if ($[10] !== initialVisiblePostsCount || $[11] !== posts || $[12] !== prepareTagTransition || $[13] !== visiblePosts) {
    t5 = async newTags => {
      const nextFiltered = getFilteredPosts(posts, newTags);
      const nextPageSize = initialVisiblePostsCount ?? Infinity;
      const nextVisible = nextFiltered.slice(0, nextPageSize);
      await prepareTagTransition(visiblePosts, nextVisible);
      setSelectedTags(newTags);
      setVisibleCount(nextPageSize);
    };
    $[10] = initialVisiblePostsCount;
    $[11] = posts;
    $[12] = prepareTagTransition;
    $[13] = visiblePosts;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleTagsChange = t5;
  let t6;
  if ($[15] !== initialVisiblePostsCount) {
    t6 = () => {
      setVisibleCount(prev => prev + (initialVisiblePostsCount ?? Infinity));
    };
    $[15] = initialVisiblePostsCount;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleShowMore = t6;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("container", css.wrapper);
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== handleTagsChange || $[19] !== selectedTags || $[20] !== tags) {
    t8 = <TagsFilter tags={tags} selectedTags={selectedTags} onTagsChange={handleTagsChange} />;
    $[18] = handleTagsChange;
    $[19] = selectedTags;
    $[20] = tags;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== handleViewModeChange || $[23] !== isMobile || $[24] !== viewMode) {
    t9 = !isMobile ? <ListViewModeToggle selectedMode={viewMode} onModeChange={handleViewModeChange} /> : null;
    $[22] = handleViewModeChange;
    $[23] = isMobile;
    $[24] = viewMode;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t8 || $[27] !== t9) {
    t10 = <div className={css.controls}>{t8}{t9}</div>;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== handleTagsChange || $[30] !== isMobile || $[31] !== viewMode || $[32] !== visiblePosts) {
    t11 = <div ref={containerRef} className={css.listContainer}>{!visiblePosts.length ? <FilterEmptyResult title={"Looks like this combo didn't match any stories\u2026 yet"} text="This filter left us hanging too. Wipe it clean and dive back into all our articles." backLabel="Back to all" className="empty-result" onClearFilters={() => handleTagsChange([])} /> : viewMode === "grid" || isMobile ? <GridView posts={visiblePosts} /> : <TableView posts={visiblePosts} />}</div>;
    $[29] = handleTagsChange;
    $[30] = isMobile;
    $[31] = viewMode;
    $[32] = visiblePosts;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== handleShowMore || $[35] !== hasMore) {
    t12 = hasMore ? <div className={css.showMoreWrapper}><AnimatedButton className={css.showMoreBtn} onClick={handleShowMore}>Show more</AnimatedButton></div> : null;
    $[34] = handleShowMore;
    $[35] = hasMore;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12) {
    t13 = <section className={t7}>{t10}{t11}{t12}</section>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  return t13;
};
export default PostsList;